import clsx from 'clsx';
import { Formik } from 'formik';
import { useState } from 'react';

import AcceptMarketingMessagesModal from 'components/curo-components/common/AcceptMarketingMessagesModal';
import { WithBackButton } from 'components/curo-components/common/BackButton';
import EmailInput from 'components/curo-components/common/EmailInput';
import PhoneNumberInput from 'components/curo-components/common/PhoneNumberInput';
import commonStyles from 'components/curo-components/forms/Forms.module.scss';
import { FormProps } from 'components/curo-components/forms/types';
import ButtonWithLoadingState from 'components/digifi-wrappers/ButtonWithLoadingState';
import Checkbox from 'components/digifi-wrappers/Checkbox';
import { createFormikHelpers } from 'utils/curo-helpers/formHelpers';

import styles from './ContactInformationForm.module.scss';
import {
  ContactInformationFields,
  IContactInformationFormValues,
  contactInformationFormLabels,
} from './constants';
import { contactInformationFormValidationSchema } from './validationSchema';

type ContactInformationFormProps = FormProps<
  ContactInformationFields,
  IContactInformationFormValues
> & {
  onBackButtonClick: () => void;
};

const ContactInformationForm = ({
  handleSubmit,
  initialData,
  onBackButtonClick,
  externalErrors,
  resetExternalErrors,
}: ContactInformationFormProps) => {
  const [isAcceptMarketingMessagesModalOpen, setIsAcceptMarketingMessagesModalOpen] =
    useState<boolean>(false);

  return (
    <Formik
      initialValues={initialData}
      validationSchema={contactInformationFormValidationSchema}
      validateOnBlur
      validateOnChange
      validateOnMount
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {({ touched, errors, values, submitForm, isValid, setFieldValue, setTouched }) => {
        const handleContinue = async () => {
          await submitForm();
        };

        const {
          handleInputChange,
          handleTouched,
          getInputErrorMessage,
          handleResetExternalError,
          hasExternalError,
        } = createFormikHelpers<ContactInformationFields, IContactInformationFormValues>(
          setFieldValue,
          setTouched,
          touched,
          errors,
          externalErrors,
          resetExternalErrors,
        );

        return (
          <section className={clsx(styles.formWrapper, commonStyles.formWrapper)}>
            <div className={styles.inputsWrapper}>
              <EmailInput
                labelTitle={contactInformationFormLabels[ContactInformationFields.BorrowerEmail]}
                value={values[ContactInformationFields.BorrowerEmail]}
                onChange={handleInputChange(ContactInformationFields.BorrowerEmail)}
                onBlur={handleTouched(ContactInformationFields.BorrowerEmail)}
                errorMessage={getInputErrorMessage(ContactInformationFields.BorrowerEmail)}
                onFocus={handleResetExternalError(ContactInformationFields.BorrowerEmail)}
                required
              />
              <PhoneNumberInput
                labelTitle={contactInformationFormLabels[ContactInformationFields.BorrowerPhone]}
                value={values[ContactInformationFields.BorrowerPhone]}
                onChange={handleInputChange(ContactInformationFields.BorrowerPhone)}
                onBlur={handleTouched(ContactInformationFields.BorrowerPhone)}
                errorMessage={getInputErrorMessage(ContactInformationFields.BorrowerPhone)}
                onFocus={handleResetExternalError(ContactInformationFields.BorrowerPhone)}
                required
              />
            </div>
            <div className={styles.checkbox}>
              <Checkbox
                onClick={() => {
                  setFieldValue(
                    ContactInformationFields.TelephoneAndElectronicCommunicationPolicy,
                    !values[ContactInformationFields.TelephoneAndElectronicCommunicationPolicy],
                  );
                }}
                checked={values[ContactInformationFields.TelephoneAndElectronicCommunicationPolicy]}
                name={ContactInformationFields.TelephoneAndElectronicCommunicationPolicy}
              />
              <span>
                I agree to{' '}
                <span
                  className={styles.underline}
                  onClick={() => {
                    if (
                      values[ContactInformationFields.TelephoneAndElectronicCommunicationPolicy]
                    ) {
                      setFieldValue(
                        ContactInformationFields.TelephoneAndElectronicCommunicationPolicy,
                        false,
                      );
                      return;
                    }
                    setIsAcceptMarketingMessagesModalOpen(true);
                  }}
                >
                  Telephone and Electronic Communication Policy
                </span>
              </span>
            </div>
            {isAcceptMarketingMessagesModalOpen && (
              <AcceptMarketingMessagesModal
                handleAccept={(isAccepted) => {
                  setFieldValue(
                    ContactInformationFields.TelephoneAndElectronicCommunicationPolicy,
                    isAccepted,
                  );
                  setIsAcceptMarketingMessagesModalOpen(false);
                }}
                onClose={() => setIsAcceptMarketingMessagesModalOpen(false)}
              />
            )}
            <WithBackButton onBackButtonClick={onBackButtonClick}>
              <ButtonWithLoadingState
                type="submit"
                kind="primary"
                size="form"
                disabled={!isValid || hasExternalError}
                onClick={handleContinue}
              >
                Continue
              </ButtonWithLoadingState>
            </WithBackButton>
          </section>
        );
      }}
    </Formik>
  );
};

export default ContactInformationForm;
